import { Link } from "react-router";
import type { IdeaRowDTO } from "@roomate/core";
import { useActors, useIdeas, useNodes } from "../api.ts";
import { ideaSteps, stuckStep, type IdeaStep } from "../lib/idea-status.ts";
import { Ago, cx } from "../ui.tsx";

/** One idea's row, kept live: the ideas query refetches on every intent and task event. */
export function useIdeaRow(intentId: string | null | undefined) {
  const ideas = useIdeas(undefined, !!intentId);
  return { row: ideas.data?.find((r) => r.intent.id === intentId) ?? null, isLoading: ideas.isLoading };
}

const MARK: Record<IdeaStep["state"], string> = {
  done: "bg-[color:var(--ok)] text-white",
  current: "bg-ink text-paper",
  upcoming: "border border-rule text-ink-3",
};

/** The idea's journey from received to on main: who holds each step, since when, and why it is stuck. */
export function IdeaStatus({ row, compact = false }: { row: IdeaRowDTO; compact?: boolean }) {
  const actors = useActors().data ?? [];
  const nodes = useNodes().data ?? [];
  const steps = ideaSteps(row, { actors, nodes });
  const stuck = stuckStep(steps);
  return (
    <div data-testid="idea-status" data-intent={row.intent.id} data-current={steps.find((s) => s.state === "current")?.key ?? "shipped"}>
      {stuck?.stuck && (
        <div role="status" data-testid="idea-stuck" className="mb-4 rounded-2xl bg-signal-soft/70 p-4">
          <p className="text-[14.5px] font-medium">Stuck: {stuck.stuck.reason}</p>
          <Link to={stuck.stuck.action.to} data-testid="idea-stuck-action" className="mt-2 inline-block rounded-full bg-ink px-4 py-2 text-[13px] text-paper">
            {stuck.stuck.action.label}
          </Link>
        </div>
      )}
      <ol className="space-y-1" aria-label="Idea progress">
        {steps.map((s, i) => (
          <li
            key={s.key}
            data-testid={`idea-step-${s.key}`}
            data-state={s.state}
            aria-current={s.state === "current" ? "step" : undefined}
            className={cx("flex gap-3 rounded-2xl px-3 py-2.5", s.state === "current" && "bg-ink/[0.05]", compact && "py-1.5")}
          >
            <span className={cx("mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full text-[11px]", MARK[s.state])} aria-hidden>
              {s.state === "done" ? "✓" : i + 1}
            </span>
            <div className="min-w-0 flex-1">
              <p className={cx("text-[14px]", s.state === "current" ? "font-medium" : s.state === "upcoming" ? "text-ink-3" : "text-ink-2")}>{s.label}</p>
              {(s.state !== "upcoming" || !compact) && <p className="text-[13px] text-ink-2">{s.detail}</p>}
              {s.state !== "upcoming" && (s.holder || s.since != null) && (
                <p className="text-[12.5px] text-ink-3" data-testid={`idea-step-${s.key}-meta`}>
                  {s.holder && <span>With {s.holder}</span>}
                  {s.holder && s.since != null && " · "}
                  {s.since != null && (
                    <span>
                      since <Ago ts={s.since} />
                    </span>
                  )}
                </p>
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
